/**
 * Login Page
 * Driver/Admin authentication with Employee ID
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Truck, AlertCircle } from 'lucide-react'
import { Button, Input, Card } from '../ui'
import { useAppStore } from '../../stores'
import { generateId } from '../../utils/helpers'

// =============================================================================
// VALIDATION SCHEMA
// =============================================================================

const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(6, 'Password must be at least 6 characters'),
})

type LoginFormData = z.infer<typeof loginSchema>

// =============================================================================
// COMPONENT
// =============================================================================

export function LoginPage() {
  const navigate = useNavigate()
  const { login, setLoading, isLoading } = useAppStore()
  const [error, setError] = useState<string | null>(null)
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })
  
  const onSubmit = async (data: LoginFormData) => {
    try {
      setError(null)
      setLoading(true)
      
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500))
      
      // Determine role based on email
      const isAdmin = data.email.includes('admin') || data.email.includes('@spt.com')
      const userRole = isAdmin ? 'admin' : 'driver'
      
      // Create user session
      login({
        id: generateId(),
        email: data.email,
        name: data.email.split('@')[0], // Use email prefix as name
        role: userRole,
      })
      
      // Navigate based on role
      navigate(userRole === 'admin' ? '/admin' : '/clock')
    } catch {
      setError('Failed to login. Please try again.')
    } finally {
      setLoading(false)
    }
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <Card className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 bg-black">
            <Truck className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-medium text-gray-900">
            SPT Driver
          </h1>
          <p className="text-gray-500 mt-1">
            Sign in to start your shift
          </p>
        </div>
        
        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        
        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <Input
            label="Email"
            type="email"
            placeholder="Enter your email address"
            error={errors.email?.message}
            {...register('email')}
          />
          
          <Input
            label="Password"
            type="password"
            placeholder="Enter your password"
            error={errors.password?.message}
            {...register('password')}
          />
          
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={isLoading}
          >
            Sign In
          </Button>
        </form>
        
        {/* Footer */}
        <p className="text-center text-sm text-gray-400 mt-8">
          Powered by Coco
        </p>
      </Card>
    </div>
  )
}
